import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Btn } from "@/components/common";
import { money } from "@/lib/format";
import { MoneyInput } from "@/lib/accountingFormat";
import { runProjection } from "@/lib/projectionEngine";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { TrendUp, TrendDown, Minus, Pause, Plus, Trash, Warning, Info, PencilSimple, CaretDown, CaretUp } from "@phosphor-icons/react";
import { DecisionDialog } from "@/components/DecisionDialog";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, PieChart, Pie, Cell } from "recharts";

const DISCLAIMER = "Projection based on the assumptions you set. Investment growth is not guaranteed. This is a planning estimate, not financial advice.";

const GROWTH_POPOVER = `Why 3%? This is an assumed yearly return on the 529 balance while the beneficiary is enrolled. Money you'll spend within a few years is usually kept in low-risk, money-market-type investments so a market drop right before tuition can't wipe it out — and those currently return around 3% (mid-2026). That's why the default is conservative rather than the higher long-term stock-market average. Adjust it: enter the rate that matches how this 529 is actually invested — higher if it's still in stocks, lower if it's in cash. This is a planning assumption, not financial advice.`;

const COST_POPOVER = `Annual education cost — the yearly cost the projection assumes for each remaining year of enrollment. School estimate: your school's published room-and-board allowance for the most recent year on file, plus any tuition and fees you've recorded this year; the projection applies this same amount to every future year, so raise it if you expect costs to climb. Last 12 months: the total education expenses you've recorded over the last 12 months — useful when your actual spending is a better guide than the published figures. Either way, it's an estimate you can type over.`;

const INFLATION_POPOVER = `Cost inflation — how much you expect education costs to rise each year. College costs have risen roughly 2–4% a year recently (about 3% projected for 2026–27), so the default is 3%. The projection raises the annual cost by this rate every future year, meaning later years cost more than today. Set it to 0% to hold costs flat, or higher if your school raises prices faster. This is a planning assumption, not a forecast.`;

// What a money box holds, as the number the projection runs on. The shared box
// only lets digits and one decimal point in, so this never has to guess.
const typedAmount = (v) => (v === "" || v == null ? 0 : parseFloat(v) || 0);

// ── Chart helper components (top-level to avoid React reconciliation issues) ──

function BalanceChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const bal = payload[0]?.payload?.balance;
  return (
    <div className="rounded-lg border border-[#C2C9D1] bg-white px-3 py-2 shadow-md">
      <p className="text-xs font-bold text-[#0A0A0A]">{label}</p>
      <p className={`text-sm font-extrabold tabular-nums ${bal < 0 ? "text-[#F01919]" : "text-[#034AA2]"}`}>
        {money(bal, "USD", { accounting: true })}
      </p>
    </div>
  );
}

function balanceChartDot(props) {
  const { cx, cy, payload, index } = props;
  const neg = payload.balance < 0;
  return (
    <circle key={index} cx={cx} cy={cy} r={4}
      fill={neg ? "#F01919" : "#034AA2"}
      stroke="#fff" strokeWidth={2} />
  );
}

export default function Projections() {
  const { selectedStudentId } = useAuth();

  if (!selectedStudentId) {
    return (
      <div>
        <PageHeader title="529 Projections" subtitle="Will your 529 cover remaining education costs?" />
        <Card className="py-12 text-center" data-testid="projections-no-student">
          <p className="text-sm text-[#6b7280]">Select a beneficiary from the switcher to view their projection.</p>
        </Card>
      </div>
    );
  }

  return <ProjectionView key={selectedStudentId} studentId={selectedStudentId} />;
}

function ProjectionView({ studentId }) {
  const { data: inputs, isLoading } = useQuery({
    queryKey: ["projection-inputs", studentId],
    queryFn: async () => (await api.get(`/projections/inputs?student_id=${studentId}`)).data,
    enabled: !!studentId,
  });

  const [balance, setBalance] = useState(null);
  const [monthsRem, setMonthsRem] = useState(null);
  const [costMode, setCostMode] = useState("coa");
  const [annualCost, setAnnualCost] = useState(null);
  const [annualContrib, setAnnualContrib] = useState(null);
  const [growthRate, setGrowthRate] = useState(3.0);
  const [costInflation, setCostInflation] = useState(3.0);
  const [pauses, setPauses] = useState([]);
  const [newDraft, setNewDraft] = useState(null); // null = no new draft; { fromMonth, fromYear, toMonth, toYear }
  const [expandedIndex, setExpandedIndex] = useState(null); // index of committed pause being edited, or null
  const [editBuf, setEditBuf] = useState(null); // buffer for editing a committed pause
  const [deleteTarget, setDeleteTarget] = useState(null); // index to confirm-delete
  const [initialized, setInitialized] = useState(false);

  // Initialize from API inputs once
  React.useEffect(() => {
    if (inputs && !initialized) {
      setBalance(inputs.current_balance);
      setMonthsRem(inputs.months_remaining ?? 48);
      setAnnualCost(inputs.expected_annual_cost || 0);
      setAnnualContrib(inputs.recent_annual_contributions || 0);
      setInitialized(true);
    }
  }, [inputs, initialized]);

  // Effective values
  // The three money boxes hold what was typed (a string while editing); the
  // projection reads the number. An emptied box counts as 0, as it did before.
  const boxBalance = balance ?? inputs?.current_balance ?? 0;
  const boxCost = costMode === "spending"
    ? (inputs?.recent_annual_spending || 0)
    : (annualCost ?? inputs?.expected_annual_cost ?? 0);
  const boxContrib = annualContrib ?? inputs?.recent_annual_contributions ?? 0;
  const effBalance = typedAmount(boxBalance);
  const effMonths = monthsRem ?? inputs?.months_remaining ?? 48;
  const effCost = typedAmount(boxCost);
  const effContrib = typedAmount(boxContrib);

  // ── Pause helpers (calendar month pickers → engine's {startMonth, durationMonths}) ──
  const nowDate = new Date();
  const projStartYear = nowDate.getFullYear();
  const projStartMonth = nowDate.getMonth() + 1; // 1-indexed

  // Convert calendar from/to into engine shape
  const calToEngine = (p) => {
    const fromOffset = (p.fromYear - projStartYear) * 12 + (p.fromMonth - projStartMonth) + 1;
    const dur = (p.toYear - p.fromYear) * 12 + (p.toMonth - p.fromMonth) + 1;
    return { startMonth: Math.max(1, fromOffset), durationMonths: Math.max(1, dur) };
  };

  // Only committed pauses feed the engine (draft is excluded)
  const enginePauses = pauses.map(calToEngine);

  const projection = useMemo(() => runProjection({
    currentBalance: effBalance,
    monthsRemaining: effMonths,
    annualCost: effCost,
    annualContribution: effContrib,
    annualGrowthRate: growthRate / 100,
    annualCostInflationRate: costInflation / 100,
    pauses: enginePauses,
  }), [effBalance, effMonths, effCost, effContrib, growthRate, costInflation, pauses]); // eslint-disable-line

  // ── New-pause draft management ──
  const openNewDraft = () => {
    setExpandedIndex(null); // close any expanded committed pause
    setNewDraft({ fromMonth: null, fromYear: null, toMonth: null, toYear: null });
  };
  const cancelNewDraft = () => setNewDraft(null);
  const commitNewDraft = () => {
    if (!newDraft || !newDraft.fromMonth || !newDraft.fromYear || !newDraft.toMonth || !newDraft.toYear) return;
    setPauses((prev) => [...prev, { fromMonth: newDraft.fromMonth, fromYear: newDraft.fromYear, toMonth: newDraft.toMonth, toYear: newDraft.toYear }]);
    setNewDraft(null); // auto-close
  };

  // ── Committed-pause expand/collapse/update ──
  const toggleExpand = (i) => {
    if (expandedIndex === i) {
      // Collapse — revert any uncommitted edits
      setExpandedIndex(null);
      setEditBuf(null);
    } else {
      // Expand — load committed values into edit buffer; close new draft
      setNewDraft(null);
      setExpandedIndex(i);
      const p = pauses[i];
      setEditBuf({ fromMonth: p.fromMonth, fromYear: p.fromYear, toMonth: p.toMonth, toYear: p.toYear });
    }
  };
  const commitEdit = () => {
    if (expandedIndex == null || !editBuf) return;
    if (!editBuf.fromMonth || !editBuf.fromYear || !editBuf.toMonth || !editBuf.toYear) return;
    setPauses((prev) => prev.map((p, i) => i === expandedIndex
      ? { fromMonth: editBuf.fromMonth, fromYear: editBuf.fromYear, toMonth: editBuf.toMonth, toYear: editBuf.toYear }
      : p));
    setExpandedIndex(null); // auto-close
    setEditBuf(null);
  };

  const confirmDeletePause = () => {
    if (deleteTarget != null) {
      setPauses((prev) => prev.filter((_, i) => i !== deleteTarget));
      if (expandedIndex === deleteTarget) { setExpandedIndex(null); setEditBuf(null); }
      setDeleteTarget(null);
    }
  };

  // ── Draft validation (for new draft) ──
  const nd = newDraft;
  const ndFromSet = nd && nd.fromMonth && nd.fromYear;
  const ndToSet = nd && nd.toMonth && nd.toYear;
  const ndBothSet = ndFromSet && ndToSet;
  const ndDur = ndBothSet ? (nd.toYear - nd.fromYear) * 12 + (nd.toMonth - nd.fromMonth) + 1 : 0;
  const ndIsBeforeNow = ndFromSet && (nd.fromYear < projStartYear || (nd.fromYear === projStartYear && nd.fromMonth < projStartMonth));
  const ndRangeValid = ndBothSet && ndDur >= 1;
  const ndCanCommit = ndBothSet && ndRangeValid && !ndIsBeforeNow;

  // ── Edit-buffer validation (for expanded committed pause) ──
  const eb = editBuf;
  const ebFromSet = eb && eb.fromMonth && eb.fromYear;
  const ebToSet = eb && eb.toMonth && eb.toYear;
  const ebBothSet = ebFromSet && ebToSet;
  const ebDur = ebBothSet ? (eb.toYear - eb.fromYear) * 12 + (eb.toMonth - eb.fromMonth) + 1 : 0;
  const ebIsBeforeNow = ebFromSet && (eb.fromYear < projStartYear || (eb.fromYear === projStartYear && eb.fromMonth < projStartMonth));
  const ebRangeValid = ebBothSet && ebDur >= 1;
  // Save is disabled unless valid AND the user actually changed something
  const ebHasChanges = expandedIndex != null && eb && pauses[expandedIndex]
    ? (eb.fromMonth !== pauses[expandedIndex].fromMonth || eb.fromYear !== pauses[expandedIndex].fromYear
      || eb.toMonth !== pauses[expandedIndex].toMonth || eb.toYear !== pauses[expandedIndex].toYear)
    : false;
  const ebCanCommit = ebBothSet && ebRangeValid && !ebIsBeforeNow && ebHasChanges;

  if (isLoading) return <div><PageHeader title="529 Projections" subtitle="Loading…" /><div className="h-32 animate-pulse rounded-xl bg-white" /></div>;

  const yearsDisplay = (effMonths / 12).toFixed(1);
  const gradLabel = projection.graduationMonth
    ? `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][projection.graduationMonth.calMonth - 1]} ${projection.graduationMonth.calYear}`
    : "—";

  return (
    <div>
      <PageHeader title="529 Projections" subtitle={inputs?.student_name ? `Scenario planning for ${inputs.student_name}` : "Will your 529 cover remaining education costs?"} />

      {/* ── Disclaimer ─────────────────────────────────── */}
      <div className="mb-4 flex items-start gap-2 rounded-lg border border-[#C2C9D1] bg-[#F0F6FF] px-3 py-2.5 text-xs text-[#475569]" data-testid="projection-disclaimer">
        <Info size={16} weight="bold" className="mt-0.5 shrink-0 text-[#034AA2]" />
        <span>{DISCLAIMER}</span>
      </div>

      {/* ── Verdict headline ───────────────────────────── */}
      <VerdictBanner projection={projection} gradLabel={gradLabel} />

      {/* ── Assumptions ────────────────────────────────── */}
      <Card className="mb-4" data-testid="projection-assumptions">
        <h3 className="mb-3 text-sm font-extrabold uppercase tracking-wide text-[#0A0A0A]">Assumptions</h3>
        <div className="grid grid-cols-1 gap-4 @sm:grid-cols-2 @xl:grid-cols-3">
          <Field label="Current 529 balance" testid="proj-balance">
            <MoneyInput currency="USD" value={boxBalance} onChange={setBalance} testid="proj-balance-input" />
            {/* balance_complete=false means some 529 account has no balance
                anchored — the prefill is a SUBTOTAL, and a projection run on it
                can show a shortfall that is not real. Say so where the number
                is edited, and stay quiet when the figure is whole. */}
            {inputs?.balance_complete === false ? (
              <p className="mt-1 text-xs font-semibold text-[#B45309]" data-testid="proj-balance-incomplete">
                {inputs.unanchored_accounts === 1
                  ? "One 529 account has no balance entered — this starting figure is missing it. Add a balance in Accounts, or type the full amount here."
                  : `${inputs.unanchored_accounts} 529 accounts have no balance entered — this starting figure is missing them. Add balances in Accounts, or type the full amount here.`}
              </p>
            ) : (
              inputs?.is_estimate && <Hint>Estimated from latest checkpoint + transactions</Hint>
            )}
          </Field>
          <Field label={`Time remaining (${yearsDisplay} yrs)`} testid="proj-months">
            <div className="flex items-center gap-2">
              <input type="range" min="1" max="120" value={effMonths}
                onChange={(e) => setMonthsRem(parseInt(e.target.value))}
                className="flex-1" data-testid="proj-months-slider" />
              <input type="number" min="1" max="120" className="inp !w-20" value={effMonths}
                onChange={(e) => setMonthsRem(Math.max(1, parseInt(e.target.value) || 1))}
                data-testid="proj-months-input" />
              <span className="text-xs text-[#6b7280]">mo</span>
            </div>
            {inputs?.enrollment_end && <Hint>Enrollment ends {inputs.enrollment_end}</Hint>}
          </Field>
          <div data-testid="proj-cost">
            <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase text-[#6b7280]">
              Annual education cost
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" aria-label="What is annual education cost?" className="rounded p-0.5 text-[#034AA2] hover:bg-[#034AA2]/10" data-testid="proj-cost-info-btn">
                    <Info size={14} weight="bold" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="max-w-sm text-xs leading-relaxed text-[#374151]" data-testid="proj-cost-popover">
                  {COST_POPOVER}
                </PopoverContent>
              </Popover>
            </span>
            <div className="mb-1.5 flex gap-2">
              <label className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold cursor-pointer ${costMode === "coa" ? "border-[#034AA2] bg-[#034AA2]/10 text-[#034AA2]" : "border-[#C2C9D1] text-[#6b7280]"}`}>
                <input type="radio" name="costMode" checked={costMode === "coa"} onChange={() => setCostMode("coa")} className="sr-only" data-testid="proj-cost-coa" />
                School estimate
              </label>
              <label className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold cursor-pointer ${costMode === "spending" ? "border-[#034AA2] bg-[#034AA2]/10 text-[#034AA2]" : "border-[#C2C9D1] text-[#6b7280]"}`}>
                <input type="radio" name="costMode" checked={costMode === "spending"} onChange={() => setCostMode("spending")} className="sr-only" data-testid="proj-cost-spending" />
                Last 12 months
              </label>
            </div>
            <MoneyInput currency="USD" value={boxCost} onChange={(v) => { setCostMode("coa"); setAnnualCost(v); }} testid="proj-cost-input" />
            {costMode === "coa" && annualCost != null && typedAmount(annualCost) !== (inputs?.expected_annual_cost ?? 0)
              ? <Hint>Custom amount you entered</Hint>
              : costMode === "coa"
                ? <Hint>Based on published costs{inputs?.coa_basis_year ? ` for ${inputs.coa_basis_year}` : ""}</Hint>
                : null}
            {costMode === "spending" && (inputs?.recent_annual_spending || 0) === 0 && (
              <p className="mt-0.5 text-[11px] font-semibold text-[#D97706]" data-testid="proj-cost-zero-hint">
                No expenses recorded in the last 12 months — this mode shows $0. Switch to School estimate or enter an amount.
              </p>
            )}
            {costMode === "spending" && (inputs?.recent_annual_spending || 0) > 0 && (
              <Hint>Your total recorded spending over the last 12 months.</Hint>
            )}
          </div>
          <Field label="Annual future contribution" testid="proj-contrib">
            <MoneyInput currency="USD" value={boxContrib} onChange={setAnnualContrib} testid="proj-contrib-input" />
            <Hint>Recent 12-month contributions: {money(inputs?.recent_annual_contributions || 0, "USD", { accounting: true })}</Hint>
          </Field>
          <div data-testid="proj-growth">
            <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase text-[#6b7280]">
              Expected annual growth %
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" aria-label="Why is the default 3%?" className="rounded p-0.5 text-[#034AA2] hover:bg-[#034AA2]/10" data-testid="proj-growth-info-btn">
                    <Info size={14} weight="bold" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="max-w-sm text-xs leading-relaxed text-[#374151]" data-testid="proj-growth-popover">
                  {GROWTH_POPOVER}
                </PopoverContent>
              </Popover>
            </span>
            <div className="flex items-center gap-2">
              <input type="range" min="0" max="15" step="0.1" value={growthRate}
                onChange={(e) => setGrowthRate(parseFloat(e.target.value))}
                className="flex-1" data-testid="proj-growth-slider" />
              <input type="number" step="0.1" min="0" max="30" className="inp !w-20" value={growthRate}
                onChange={(e) => setGrowthRate(Math.max(0, parseFloat(e.target.value) || 0))}
                data-testid="proj-growth-input" />
              <span className="text-xs text-[#6b7280]">%</span>
            </div>
            <Hint>Conservative default while enrolled — adjust to your plan.</Hint>
          </div>
          <div data-testid="proj-inflation">
            <span className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase text-[#6b7280]">
              Expected cost inflation %
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" aria-label="What is cost inflation?" className="rounded p-0.5 text-[#034AA2] hover:bg-[#034AA2]/10" data-testid="proj-inflation-info-btn">
                    <Info size={14} weight="bold" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="max-w-sm text-xs leading-relaxed text-[#374151]" data-testid="proj-inflation-popover">
                  {INFLATION_POPOVER}
                </PopoverContent>
              </Popover>
            </span>
            <div className="flex items-center gap-2">
              <input type="range" min="0" max="10" step="0.1" value={costInflation}
                onChange={(e) => setCostInflation(parseFloat(e.target.value))}
                className="flex-1" data-testid="proj-inflation-slider" />
              <input type="number" step="0.1" min="0" max="20" className="inp !w-20" value={costInflation}
                onChange={(e) => setCostInflation(Math.max(0, parseFloat(e.target.value) || 0))}
                data-testid="proj-inflation-input" />
              <span className="text-xs text-[#6b7280]">%</span>
            </div>
            <Hint>Education costs typically rise 2–4% per year.</Hint>
          </div>
        </div>

        {/* ── Pauses ──────────────────────────────────── */}
        <div className="mt-4 border-t border-[#E9E9EB] pt-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold uppercase tracking-wide text-[#6b7280]">Enrollment pauses / gaps</h4>
            {!newDraft && (
              <button onClick={openNewDraft} className="flex items-center gap-1 text-xs font-semibold text-[#034AA2] hover:underline" data-testid="proj-add-pause">
                <Plus size={14} weight="bold" /> Add pause
              </button>
            )}
          </div>
          {pauses.length === 0 && !newDraft && <p className="mt-1 text-xs text-[#9aa1a9]">No pauses — education costs run continuously until graduation.</p>}

          {/* Committed pause rows (summary + optional inline editor) */}
          {pauses.map((p, i) => {
            const dur = (p.toYear - p.fromYear) * 12 + (p.toMonth - p.fromMonth) + 1;
            const isExpanded = expandedIndex === i;
            return (
              <div key={i} className="mt-2 rounded-lg border border-[#E5E7EB] bg-white" data-testid={`proj-pause-${i}`}>
                {/* Summary row */}
                <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <Pause size={14} weight="bold" className="shrink-0 text-[#034AA2]" />
                  <span className="text-sm font-medium text-[#0A0A0A]" data-testid={`proj-pause-summary-${i}`}>
                    {MONTH_NAMES[p.fromMonth - 1]} {p.fromYear} &rarr; {MONTH_NAMES[p.toMonth - 1]} {p.toYear}
                  </span>
                  <span className="rounded-md bg-[#034AA2]/10 px-2 py-0.5 text-xs font-bold text-[#034AA2] tabular-nums" data-testid={`proj-pause-dur-${i}`}>
                    {dur} month{dur !== 1 ? "s" : ""}
                  </span>
                  <span className="text-[11px] text-[#6b7280]">graduation shifts to {gradLabel}</span>
                  <div className="ml-auto flex shrink-0 gap-1">
                    <button onClick={() => toggleExpand(i)} className="flex items-center gap-0.5 rounded p-1.5 text-[#034AA2] hover:bg-[#034AA2]/10" data-testid={`proj-pause-edit-${i}`} aria-label={isExpanded ? "Collapse pause" : "Expand pause"}>
                      <PencilSimple size={13} weight="bold" />
                      {isExpanded
                        ? <CaretUp size={13} weight="bold" />
                        : <CaretDown size={13} weight="bold" />}
                    </button>
                    <button onClick={() => setDeleteTarget(i)} className="rounded p-1.5 text-[#F01919] hover:bg-[#F01919]/10" data-testid={`proj-pause-remove-${i}`} aria-label="Remove pause">
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </div>
                {/* Inline editor (expanded) */}
                {isExpanded && editBuf && (
                  <PauseInlineEditor
                    buf={editBuf}
                    setBuf={setEditBuf}
                    canCommit={ebCanCommit}
                    bothSet={ebBothSet}
                    rangeValid={ebRangeValid}
                    dur={ebDur}
                    isBeforeNow={ebIsBeforeNow}
                    onCommit={commitEdit}
                    onCancel={() => toggleExpand(i)}
                    commitLabel="Save"
                    testidPrefix="proj-pause-edit-form"
                  />
                )}
              </div>
            );
          })}

          {/* New pause draft editor */}
          {newDraft && (
            <PauseInlineEditor
              buf={newDraft}
              setBuf={setNewDraft}
              canCommit={ndCanCommit}
              bothSet={ndBothSet}
              rangeValid={ndRangeValid}
              dur={ndDur}
              isBeforeNow={ndIsBeforeNow}
              onCommit={commitNewDraft}
              onCancel={cancelNewDraft}
              commitLabel="Save"
              testidPrefix="proj-pause-draft"
              isNew
            />
          )}
        </div>
      </Card>

      {/* ── Delete pause confirmation ──────────────────── */}
      <DecisionDialog
        open={deleteTarget != null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeletePause}
        title="Remove this pause?"
        actionLabel="Remove pause"
        testid="proj-pause-delete"
      >
        <p className="text-sm text-[#374151]">Your projection will update.</p>
      </DecisionDialog>

      {/* ── Fund-balance chart ─────────────────────────── */}
      {projection.yearSummary.length > 0 && <BalanceChart yearSummary={projection.yearSummary} />}

      {/* ── Year-by-year table ─────────────────────────── */}
      {projection.yearSummary.length > 0 && (
        <Card className="overflow-x-auto p-0" data-testid="projection-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#C2C9D1] text-left text-xs uppercase tracking-wide text-[#1f2937]">
                <th className="px-3 py-2.5">Year</th>
                <th className="px-3 py-2.5 text-right">Start Balance</th>
                <th className="px-3 py-2.5 text-right">Contributions</th>
                <th className="px-3 py-2.5 text-right">Growth</th>
                <th className="px-3 py-2.5 text-right">Education Cost</th>
                <th className="px-3 py-2.5 text-right">End Balance</th>
              </tr>
            </thead>
            <tbody>
              {projection.yearSummary.map((yr) => (
                <tr key={yr.year} className="border-t border-[#E9E9EB] hover:bg-[#F7F8FA]" data-testid={`proj-year-${yr.year}`}>
                  <td className="px-3 py-2.5 font-semibold">
                    {yr.year}
                    {yr.hasPause && <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-[#D97706]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#D97706]"><Pause size={10} weight="bold" /> pause</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{money(yr.startBalance, "USD", { accounting: true })}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[#077A39]">{money(yr.contributions, "USD", { accounting: true })}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[#077A39]">{money(yr.growth, "USD", { accounting: true })}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-[#F01919]">{money(-yr.cost, "USD", { accounting: true })}</td>
                  <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${yr.endBalance < 0 ? "text-[#F01919]" : yr.endBalance < 1000 ? "text-[#D97706]" : "text-[#0A0A0A]"}`}>
                    {money(yr.endBalance, "USD", { accounting: true })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* ── Spending by category pie chart ──────────────── */}
      <SpendingByCategory studentId={studentId} />

      {/* ── Disclaimer footer ──────────────────────────── */}
      <p className="mt-4 text-center text-xs text-[#9aa1a9]" data-testid="projection-disclaimer-footer">{DISCLAIMER}</p>

      <style>{`.inp{height:40px;width:100%;border:1px solid #C2C9D1;border-radius:6px;padding:0 8px;font-size:14px;background:#fff}`}</style>
    </div>
  );
}

function BalanceChart({ yearSummary }) {
  // Build chart data: each year gets a point. For coloring, we split into positive and negative segments.
  const data = yearSummary.map((yr) => ({
    year: yr.year,
    balance: yr.endBalance,
    positive: yr.endBalance >= 0 ? yr.endBalance : 0,
    negative: yr.endBalance < 0 ? yr.endBalance : null,
  }));

  const hasNegative = data.some((d) => d.balance < 0);
  const yMin = Math.min(0, ...data.map((d) => d.balance));
  const yMax = Math.max(0, ...data.map((d) => d.balance));
  // Pad y-axis a bit
  const yDomain = [Math.floor(yMin * 1.1), Math.ceil(yMax * 1.1) || 1000];

  return (
    <Card className="mb-4" data-testid="projection-balance-chart">
      <h3 className="mb-3 text-xs font-bold uppercase tracking-wide text-[#6b7280]">Projected 529 Balance Over Time</h3>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E9E9EB" />
            <XAxis dataKey="year" tick={{ fontSize: 12, fill: "#6b7280" }} />
            <YAxis
              tickFormatter={(v) => {
                if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(0)}k`;
                return `$${v}`;
              }}
              tick={{ fontSize: 11, fill: "#6b7280" }}
              domain={yDomain}
            />
            <Tooltip content={<BalanceChartTooltip />} />
            {hasNegative && <ReferenceLine y={0} stroke="#1f2937" strokeDasharray="3 3" strokeWidth={1.5} />}
            {/* Positive area in navy */}
            <defs>
              <linearGradient id="balGradPos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#034AA2" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#034AA2" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="balGradNeg" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F01919" stopOpacity={0.05} />
                <stop offset="100%" stopColor="#F01919" stopOpacity={0.3} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="balance"
              stroke="#034AA2"
              strokeWidth={2.5}
              fill="url(#balGradPos)"
              dot={balanceChartDot}
              activeDot={{ r: 6, strokeWidth: 2, fill: "#034AA2", stroke: "#fff" }}
            />
            {/* Overlay negative portion in red */}
            {hasNegative && (
              <Area
                type="monotone"
                dataKey="negative"
                stroke="#F01919"
                strokeWidth={2.5}
                fill="url(#balGradNeg)"
                connectNulls={false}
                dot={false}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

// ── Category grouping + Okabe-Ito colorblind-safe palette (no purple, no semantic green/red) ──
const CATEGORY_GROUP_MAP = {
  "Room & Board": "Room & Board", "Housing": "Room & Board", "Food": "Room & Board",
  "Rent / Room & Board": "Room & Board", "Meal Plan": "Room & Board",
  "Tuition & Fees": "Tuition & Fees", "Tuition": "Tuition & Fees", "Fees": "Tuition & Fees",
};
const catGroup = (cat) => CATEGORY_GROUP_MAP[cat] || cat;
const OKABE_ITO = ["#0072B2", "#E69F00", "#009E73", "#D55E00", "#56B4E9", "#CC9A00", "#666666", "#000000"];

function buildGroupedPie(breakdown) {
  const groupMap = new Map();
  for (const b of breakdown) {
    const g = catGroup(b.category);
    if (!groupMap.has(g)) groupMap.set(g, { group: g, total: 0, categories: [] });
    const entry = groupMap.get(g);
    entry.total += b.total_usd;
    entry.categories.push(b);
  }
  const groups = [...groupMap.values()].sort((a, b) => b.total - a.total);
  groups.forEach((g, i) => { g.color = OKABE_ITO[i % OKABE_ITO.length]; });
  groups.forEach((g) => { g.total = Math.round(g.total * 100) / 100; });
  return groups;
}

function GroupedPieTooltip({ active, payload, total }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const pct = total > 0 ? ((d.total / total) * 100).toFixed(1) : "0.0";
  return (
    <div className="rounded-lg border border-[#C2C9D1] bg-white px-3 py-2 shadow-md">
      <p className="text-xs font-bold text-[#0A0A0A]">{d.group}</p>
      <p className="text-sm font-extrabold tabular-nums" style={{ color: d.color }}>{money(d.total, "USD", { accounting: true })}</p>
      <p className="text-[11px] text-[#6b7280]">{pct}% of total</p>
    </div>
  );
}

function SpendingByCategory({ studentId }) {
  const { data: allExpenses = [] } = useQuery({
    queryKey: ["expenses-for-years", studentId],
    queryFn: async () => (await api.get(`/expenses?student_id=${studentId}`)).data,
    enabled: !!studentId,
  });
  const availableYears = useMemo(() => {
    const yrs = [...new Set(allExpenses.map((e) => e.eligibility_tax_year).filter(Boolean))];
    return yrs.sort((a, b) => b - a);
  }, [allExpenses]);

  const [selectedYear, setSelectedYear] = useState("");

  const { data: breakdown = [], isLoading } = useQuery({
    queryKey: ["category-breakdown", studentId, selectedYear],
    queryFn: async () => {
      const url = `/expenses/category-breakdown?student_id=${studentId}${selectedYear ? `&tax_year=${selectedYear}` : ""}`;
      return (await api.get(url)).data;
    },
    enabled: !!studentId,
  });

  const groups = useMemo(() => buildGroupedPie(breakdown), [breakdown]);
  const total = groups.reduce((s, g) => s + g.total, 0);

  return (
    <Card className="mt-4" data-testid="spending-by-category">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold uppercase tracking-wide text-[#6b7280]">Spending by Category</h3>
        <select
          className="h-8 rounded-md border border-[#C2C9D1] bg-white px-2 text-xs font-semibold"
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          data-testid="category-year-select"
        >
          <option value="">All years combined</option>
          {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {isLoading ? (
        <div className="h-48 animate-pulse rounded-lg bg-[#E9E9EB]" />
      ) : breakdown.length === 0 ? (
        <div className="flex h-48 items-center justify-center rounded-lg border border-dashed border-[#C2C9D1] text-sm text-[#6b7280]" data-testid="category-empty">
          No expenses for this selection.
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4 lg:flex-row lg:items-start">
          <div className="h-64 w-full max-w-xs shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={groups}
                  dataKey="total"
                  nameKey="group"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  stroke="#fff"
                  strokeWidth={1.5}
                  label={false}
                >
                  {groups.map((g) => (
                    <Cell key={g.group} fill={g.color} />
                  ))}
                </Pie>
                <Tooltip content={<GroupedPieTooltip total={total} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 min-w-0">
            <div className="space-y-2.5">
              {groups.map((g) => {
                const gPct = total > 0 ? ((g.total / total) * 100).toFixed(1) : "0.0";
                const isMulti = g.categories.length > 1;
                return (
                  <div key={g.group} data-testid={`category-group-${g.group.replace(/\s+/g, "-")}`}>
                    <div className={`flex items-center gap-2 text-sm ${isMulti ? "font-bold" : "font-medium"}`}>
                      <span className="h-3 w-3 shrink-0 rounded-sm" style={{ background: g.color }} />
                      <span className="min-w-0 flex-1 truncate">{g.group}</span>
                      <span className="shrink-0 tabular-nums font-semibold">{money(g.total, "USD", { accounting: true })}</span>
                      <span className="shrink-0 text-xs text-[#6b7280]">{gPct}%</span>
                    </div>
                    {isMulti && g.categories.map((c) => {
                      const cPct = total > 0 ? ((c.total_usd / total) * 100).toFixed(1) : "0.0";
                      return (
                        <div key={c.category} className="ml-5 mt-0.5 flex items-center gap-2 text-xs text-[#475569]" data-testid={`category-row-${c.category.replace(/\s+/g, "-")}`}>
                          <span className="h-2 w-2 shrink-0 rounded-sm" style={{ background: g.color, opacity: 0.6 }} />
                          <span className="min-w-0 flex-1 truncate">{c.category}</span>
                          <span className="shrink-0 tabular-nums font-semibold">{money(c.total_usd, "USD", { accounting: true })}</span>
                          <span className="shrink-0 text-[#6b7280]">{cPct}%</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            <div className="mt-3 border-t border-[#E9E9EB] pt-2 flex items-center justify-between text-sm">
              <span className="font-bold text-[#0A0A0A]">Total</span>
              <span className="font-extrabold tabular-nums" data-testid="category-total">{money(total, "USD", { accounting: true })}</span>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}


function VerdictBanner({ projection, gradLabel }) {
  if (projection.verdict === "no_data" || projection.verdict === "complete") return null;

  const { verdict, endBalance, shortfallMonth } = projection;
  const config = {
    surplus: {
      bg: "border-[#077A39]/30 bg-[#077A39]/5",
      text: "text-[#077A39]",
      icon: <TrendUp size={22} weight="bold" className="text-[#077A39]" />,
      headline: `On track — projected surplus of ${money(endBalance, "USD", { accounting: true })} at graduation (${gradLabel})`,
    },
    tight: {
      bg: "border-[#D97706]/30 bg-[#FEF3C7]/40",
      text: "text-[#92400E]",
      icon: <Minus size={22} weight="bold" className="text-[#D97706]" />,
      headline: `Covers costs, thin margin — ~${money(endBalance, "USD", { accounting: true })} left at graduation (${gradLabel})`,
    },
    shortfall: {
      bg: "border-[#F01919]/30 bg-[#FBE9E9]",
      text: "text-[#6F0A11]",
      icon: <TrendDown size={22} weight="bold" className="text-[#F01919]" />,
      headline: shortfallMonth
        ? `Projected shortfall of ${money(shortfallMonth.shortfall, "USD", { accounting: true })} in ${shortfallMonth.label}`
        : `Projected shortfall — balance depleted before graduation`,
    },
  };
  const c = config[verdict];

  return (
    <div className={`mb-4 flex items-start gap-3 rounded-xl border px-4 py-4 ${c.bg}`} data-testid="projection-verdict">
      <div className="mt-0.5 shrink-0">{c.icon}</div>
      <div>
        <div className={`text-base font-extrabold ${c.text}`} data-testid="projection-verdict-text">{c.headline}</div>
        {verdict === "shortfall" && (
          <p className="mt-1 text-xs text-[#6b7280]">Consider increasing contributions, adjusting the growth assumption, or reviewing cost estimates.</p>
        )}
      </div>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <span className="mb-1 block text-xs font-bold uppercase text-[#6b7280]">{label}</span>
      {children}
    </div>
  );
}

function Hint({ children }) {
  return <p className="mt-0.5 text-[11px] text-[#9aa1a9]">{children}</p>;
}



/** Shared inline editor used for both new drafts and editing committed pauses. */
function PauseInlineEditor({ buf, setBuf, canCommit, bothSet, rangeValid, dur, isBeforeNow, onCommit, onCancel, commitLabel, testidPrefix, isNew }) {
  return (
    <div className={`${isNew ? "mt-2 rounded-lg border-2 border-dashed border-[#034AA2]/40 bg-[#F0F6FF]" : "border-t border-[#E5E7EB] bg-[#F9FAFB]"} p-3`} data-testid={`${testidPrefix}`}>
      <div className="flex flex-wrap items-end gap-3">
        <PauseMonthPicker label="From" year={buf.fromYear} month={buf.fromMonth}
          onYearChange={(v) => setBuf((d) => ({ ...d, fromYear: v }))}
          onMonthChange={(v) => setBuf((d) => ({ ...d, fromMonth: v }))}
          testid={`${testidPrefix}-from`} allowEmpty={isNew} />
        <PauseMonthPicker label="To" year={buf.toYear} month={buf.toMonth}
          onYearChange={(v) => setBuf((d) => ({ ...d, toYear: v }))}
          onMonthChange={(v) => setBuf((d) => ({ ...d, toMonth: v }))}
          testid={`${testidPrefix}-to`} allowEmpty={isNew} />
        {bothSet && (
          <span className={`rounded-md px-2.5 py-1.5 text-xs font-bold tabular-nums ${rangeValid ? "bg-[#034AA2]/10 text-[#034AA2]" : "bg-[#F01919]/10 text-[#F01919]"}`} data-testid={`${testidPrefix}-dur`}>
            {rangeValid ? `${dur} month${dur !== 1 ? "s" : ""}` : "—"}
          </span>
        )}
      </div>
      {bothSet && !rangeValid && (
        <p className="mt-1.5 text-[11px] font-semibold text-[#F01919]" data-testid={`${testidPrefix}-error`}>
          &ldquo;To&rdquo; must be the same as or after &ldquo;From&rdquo;.
        </p>
      )}
      {isBeforeNow && rangeValid && (
        <p className="mt-1.5 text-[11px] text-[#D97706]" data-testid={`${testidPrefix}-hint`}>
          &ldquo;From&rdquo; is before the current month — pause starts immediately.
        </p>
      )}
      <div className="mt-2 flex gap-2">
        <button onClick={onCommit} disabled={!canCommit}
          className="inline-flex items-center gap-1 rounded-md bg-[#034AA2] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#023580] disabled:opacity-40"
          data-testid={`${testidPrefix}-commit`}>
          {commitLabel}
        </button>
        <button onClick={onCancel}
          className="rounded-md border border-[#C2C9D1] px-3 py-1.5 text-xs font-semibold text-[#374151] hover:bg-[#F3F4F6]"
          data-testid={`${testidPrefix}-cancel`}>
          Cancel
        </button>
      </div>
    </div>
  );
}

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function PauseMonthPicker({ label, year, month, onYearChange, onMonthChange, testid, allowEmpty }) {
  const curYear = new Date().getFullYear();
  const years = [];
  for (let y = curYear; y <= curYear + 10; y++) years.push(y);

  return (
    <div data-testid={testid}>
      <span className="mb-0.5 block text-[10px] font-bold uppercase text-[#9aa1a9]">{label}</span>
      <div className="flex gap-1">
        <select
          className="h-9 rounded-md border border-[#C2C9D1] bg-white px-2 text-sm"
          value={month || ""}
          onChange={(e) => onMonthChange(e.target.value ? parseInt(e.target.value) : null)}
          data-testid={`${testid}-month`}
        >
          {allowEmpty && <option value="">Month</option>}
          {MONTH_NAMES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
        </select>
        <select
          className="h-9 rounded-md border border-[#C2C9D1] bg-white px-2 text-sm"
          value={year || ""}
          onChange={(e) => onYearChange(e.target.value ? parseInt(e.target.value) : null)}
          data-testid={`${testid}-year`}
        >
          {allowEmpty && <option value="">Year</option>}
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
    </div>
  );
}
